// `v-digits-only` limits an input to the digits 0-9; `v-digits-only.decimal`
// also allows a single decimal point. An optional value caps the number of
// digits before the decimal point: `v-digits-only.decimal="{ maxDigits: 11 }"`.
// Put it on an <input> or on a component that wraps one (e.g. a Vuetify
// v-text-field): the listeners sit on the root element and catch events
// bubbling up from the inner input.
// type="number" inputs otherwise accept e, E, +, - and . because those are valid
// in numbers like 1e5 or -2.5. For `.decimal`, use type="text" (with
// inputmode="decimal"): number inputs hide their value and cursor while the
// text is partial (e.g. "12."), so a second "." could not be detected.
const INTEGER = /^\d+$/;
const DECIMAL = /^\d*\.?\d*$/;

// The value the input would hold if `text` replaced the current selection.
// Number inputs expose no selection, so fall back to checking `text` alone.
function nextValue(input, text) {
  try {
    const { value, selectionStart, selectionEnd } = input;
    if (selectionStart == null) return text;
    return value.slice(0, selectionStart) + text + value.slice(selectionEnd);
  } catch (e) {
    return text;
  }
}

function isAllowed(el, input, text) {
  const pattern = el._digitsOnlyDecimal ? DECIMAL : INTEGER;
  const candidate = nextValue(input, text.trim());
  if (candidate === "") return true;
  if (!pattern.test(candidate)) return false;
  const maxDigits = el._digitsOnlyMaxDigits;
  return !maxDigits || candidate.split(".")[0].length <= maxDigits;
}

function readOptions(el, binding) {
  el._digitsOnlyDecimal = !!binding.modifiers.decimal;
  el._digitsOnlyMaxDigits = (binding.value && binding.value.maxDigits) || null;
}

function onKeydown(event) {
  // Leave shortcuts (copy, paste, select all) and named keys (Backspace,
  // arrows, Tab, Enter) alone; only single printable characters are filtered.
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  if (!event.key || event.key.length !== 1) return;
  if (!isAllowed(event.currentTarget, event.target, event.key)) {
    event.preventDefault();
  }
}

function onPaste(event) {
  const clipboard = event.clipboardData || window.clipboardData;
  if (clipboard && !isAllowed(event.currentTarget, event.target, clipboard.getData("text"))) {
    event.preventDefault();
  }
}

function onDrop(event) {
  const transfer = event.dataTransfer;
  if (transfer && !isAllowed(event.currentTarget, event.target, transfer.getData("text"))) {
    event.preventDefault();
  }
}

export default {
  install(Vue) {
    Vue.directive("digits-only", {
      bind(el, binding) {
        readOptions(el, binding);
        el.addEventListener("keydown", onKeydown);
        el.addEventListener("paste", onPaste);
        el.addEventListener("drop", onDrop);
      },
      update(el, binding) {
        readOptions(el, binding);
      },
      unbind(el) {
        el.removeEventListener("keydown", onKeydown);
        el.removeEventListener("paste", onPaste);
        el.removeEventListener("drop", onDrop);
        delete el._digitsOnlyDecimal;
        delete el._digitsOnlyMaxDigits;
      },
    });
  },
};
